/** Detail + edit drawer for one aux record — and the "add record" mode.
 *
 *  The edit form is generated from the kind's `edit_schema` (served by the
 *  backend kind registry): enum → select, boolean → switch, everything
 *  scalar → text field. Arrays are shown read-only — none of the editable
 *  kinds require array editing, and a JSON textarea would invite typos the
 *  server would bounce at approve time anyway.
 *
 *  Edits stage locally (useReviewState) and travel in the approval payload;
 *  nothing is written until the task's materialize step runs.
 */
import CloseIcon from '@mui/icons-material/Close';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  FormControlLabel,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';

import * as auxSyncApi from '@/api/auxSync';
import type { AuxKindSpec, AuxRecordSummary } from '@/api/auxSync';
import { act, ValueActions } from '@/components/ValueActions/ValueActions';
import { LinkifiedText } from '@/features/bgtasks/taskFormat';
import {
  accountActions,
  configTargetActions,
  integrationActions,
  type TaskCtx,
} from '@/features/bgtasks/taskLinks';

interface SchemaProp {
  type?: string | string[];
  enum?: (string | null)[];
  pattern?: string;
}

function propKind(prop: SchemaProp): 'enum' | 'boolean' | 'array' | 'text' {
  if (prop.enum) return 'enum';
  const types = Array.isArray(prop.type) ? prop.type : [prop.type];
  if (types.includes('boolean')) return 'boolean';
  if (types.includes('array')) return 'array';
  return 'text';
}

function SchemaForm({
  spec,
  values,
  onChange,
  disabledFields = [],
}: {
  spec: AuxKindSpec;
  values: Record<string, unknown>;
  onChange: (field: string, value: unknown) => void;
  disabledFields?: string[];
}) {
  const properties = (spec.edit_schema?.properties ?? {}) as Record<string, SchemaProp>;
  const required = new Set((spec.edit_schema?.required as string[] | undefined) ?? []);
  return (
    <Stack spacing={1.5}>
      {Object.entries(properties).map(([field, prop]) => {
        const kind = propKind(prop);
        const label = field.replace(/_/g, ' ');
        const value = values[field];
        const highlight = spec.selection_requires.includes(field);
        if (kind === 'array') {
          const items = Array.isArray(value) ? value : [];
          if (!items.length) return null;
          return (
            <Box key={field}>
              <Typography variant="caption" color="text.secondary">
                {label}
              </Typography>
              <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
                {items.map((x, i) => (
                  <Chip key={i} size="small" label={String(x)} />
                ))}
              </Stack>
            </Box>
          );
        }
        if (kind === 'boolean') {
          return (
            <FormControlLabel
              key={field}
              control={
                <Switch
                  size="small"
                  checked={Boolean(value)}
                  onChange={(e) => onChange(field, e.target.checked)}
                  disabled={disabledFields.includes(field)}
                />
              }
              label={label}
            />
          );
        }
        if (kind === 'enum') {
          const options = (prop.enum ?? []).filter((o): o is string => o != null && o !== '');
          return (
            <TextField
              key={field}
              select
              size="small"
              label={label}
              value={(value as string) ?? ''}
              required={required.has(field) || highlight}
              color={highlight && !value ? 'warning' : undefined}
              focused={highlight && !value ? true : undefined}
              onChange={(e) => onChange(field, e.target.value || null)}
              disabled={disabledFields.includes(field)}
            >
              {!required.has(field) && <MenuItem value="">—</MenuItem>}
              {options.map((o) => (
                <MenuItem key={o} value={o}>
                  {o}
                </MenuItem>
              ))}
            </TextField>
          );
        }
        return (
          <TextField
            key={field}
            size="small"
            label={label}
            value={value == null ? '' : String(value)}
            required={required.has(field)}
            color={highlight && !value ? 'warning' : undefined}
            helperText={highlight && !value ? 'Required before this record can be applied' : undefined}
            onChange={(e) => onChange(field, e.target.value === '' ? null : e.target.value)}
            disabled={disabledFields.includes(field)}
          />
        );
      })}
    </Stack>
  );
}

function DiffTable({ diff }: { diff: { field: string; before: unknown; after: unknown }[] }) {
  if (!diff.length) return null;
  return (
    <Box>
      <Typography variant="subtitle2" gutterBottom>
        Changes vs current
      </Typography>
      <Stack spacing={0.5}>
        {diff.map((d) => (
          <Typography key={d.field} variant="body2">
            <strong>{d.field}</strong>:{' '}
            <Box component="span" sx={{ textDecoration: 'line-through', color: 'text.secondary' }}>
              {d.before == null ? '—' : String(d.before)}
            </Box>{' '}
            → {d.after == null ? '—' : String(d.after)}
          </Typography>
        ))}
      </Stack>
    </Box>
  );
}

export function RecordDetailDrawer({
  taskId,
  tenantId,
  row,
  spec,
  editable,
  localEdit,
  onSaveEdit,
  onClearEdit,
  onClose,
}: {
  taskId: string;
  /** The TASK's tenant — the drawer has no tenant of its own, and every
   *  operator-scoped link (integrations, configuration) needs it. */
  tenantId: number | null;
  row: AuxRecordSummary | null;
  spec: AuxKindSpec | null;
  editable: boolean;
  localEdit: Record<string, unknown> | undefined;
  onSaveEdit: (key: string, data: Record<string, unknown>) => void;
  onClearEdit: (key: string) => void;
  onClose: () => void;
}) {
  const ctx: TaskCtx = { tenantId };
  const detailQuery = useQuery({
    queryKey: ['aux-sync', 'record', taskId, row?.key],
    queryFn: () => auxSyncApi.recordDetail(taskId, row!.key),
    enabled: Boolean(row),
    staleTime: Infinity,
  });
  const record = detailQuery.data?.record;

  const [draft, setDraft] = useState<Record<string, unknown>>({});
  useEffect(() => {
    setDraft({ ...(row?.data ?? {}), ...(localEdit ?? {}) });
  }, [row?.key, row?.data, localEdit]);

  const dirty = useMemo(() => {
    if (!row) return false;
    return Object.entries(draft).some(
      ([k, v]) => JSON.stringify(v ?? null) !== JSON.stringify((row.data[k] as unknown) ?? null),
    );
  }, [draft, row]);

  return (
    <Drawer anchor="right" open={Boolean(row)} onClose={onClose}>
      <Box sx={{ width: 440, p: 2 }}>
        <Stack direction="row" alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="h6" sx={{ flex: 1, overflowWrap: 'anywhere' }}>
            {spec?.label ?? row?.kind}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Stack>
        {row && (
          <Stack direction="row" spacing={0.5} sx={{ mb: 2 }} useFlexGap flexWrap="wrap">
            <Chip size="small" variant="outlined" label={row.status} />
            <Chip size="small" variant="outlined" label={`source: ${row.source}`} />
            <ValueActions chip actions={integrationActions(row.restech, ctx)}>
              <Chip size="small" variant="outlined" label={row.restech} />
            </ValueActions>
            {row.account && (
              <ValueActions chip actions={accountActions(row.account, ctx)}>
                <Chip size="small" variant="outlined" label={row.account} />
              </ValueActions>
            )}
            {row.confidence != null && (
              <Chip size="small" variant="outlined" label={`${Math.round(row.confidence * 100)}%`} />
            )}
            {localEdit && <Chip size="small" color="info" label="edited" />}
          </Stack>
        )}

        {row && row.issues.length > 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {row.issues.join(' · ')}
          </Alert>
        )}

        {row && spec && spec.edit_schema && (
          <>
            <SchemaForm
              spec={spec}
              values={draft}
              onChange={(field, value) => setDraft((d) => ({ ...d, [field]: value }))}
            />
            {editable && (
              <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                <Button
                  variant="contained"
                  size="small"
                  disabled={!dirty}
                  onClick={() => {
                    const changed: Record<string, unknown> = {};
                    for (const [k, v] of Object.entries(draft)) {
                      if (
                        JSON.stringify(v ?? null) !==
                        JSON.stringify((row.data[k] as unknown) ?? null)
                      ) {
                        changed[k] = v;
                      }
                    }
                    onSaveEdit(row.key, changed);
                    onClose();
                  }}
                >
                  Stage edit
                </Button>
                {localEdit && (
                  <Button size="small" onClick={() => onClearEdit(row.key)}>
                    Revert to extracted
                  </Button>
                )}
              </Stack>
            )}
            <Divider sx={{ my: 2 }} />
          </>
        )}

        {detailQuery.isLoading && <CircularProgress size={20} />}
        {record && (
          <Stack spacing={2}>
            <DiffTable diff={record.diff ?? []} />
            {record.current != null && (
              <Box>
                <Stack direction="row" alignItems="baseline" spacing={1}>
                  <Typography variant="subtitle2" gutterBottom sx={{ flexGrow: 1 }}>
                    Currently configured
                  </Typography>
                  <ValueActions
                    actions={[act.copy(JSON.stringify(record.current, null, 2), 'Copy JSON')]}
                  >
                    <Typography variant="caption" color="primary">
                      copy JSON
                    </Typography>
                  </ValueActions>
                </Stack>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>
                  {JSON.stringify(record.current, null, 1)}
                </Typography>
              </Box>
            )}
            {/* A config-target kind writes into the operator's own settings,
                so the Configuration tab is where the result lands. */}
            {spec?.target === 'config' && configTargetActions(ctx).length > 0 && (
              <ValueActions actions={configTargetActions(ctx)}>
                <Typography variant="caption" color="primary">
                  Applies to the operator&apos;s configuration
                </Typography>
              </ValueActions>
            )}
            {record.evidence.length > 0 && (
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Evidence
                </Typography>
                <Stack spacing={0.5}>
                  {record.evidence.map((e, i) => (
                    <Typography key={i} variant="body2" color="text.secondary">
                      <LinkifiedText text={e} />
                    </Typography>
                  ))}
                </Stack>
              </Box>
            )}
            {Object.keys(record.proposed ?? {}).length > 0 && (
              <Box>
                <Stack direction="row" alignItems="baseline" spacing={1}>
                  <Typography variant="subtitle2" gutterBottom sx={{ flexGrow: 1 }}>
                    Will apply
                  </Typography>
                  <ValueActions
                    actions={[act.copy(JSON.stringify(record.proposed, null, 2), 'Copy JSON')]}
                  >
                    <Typography variant="caption" color="primary">
                      copy JSON
                    </Typography>
                  </ValueActions>
                </Stack>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>
                  {JSON.stringify(record.proposed, null, 1)}
                </Typography>
              </Box>
            )}
            <Typography variant="caption" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
              {row?.key}
            </Typography>
          </Stack>
        )}
      </Box>
    </Drawer>
  );
}

/** Add-record dialog body (rendered inside a Drawer by the review page). */
export function AddRecordForm({
  spec,
  onSubmit,
  onCancel,
}: {
  spec: AuxKindSpec;
  onSubmit: (data: Record<string, unknown>) => void;
  onCancel: () => void;
}) {
  const [draft, setDraft] = useState<Record<string, unknown>>({});
  const required = (spec.edit_schema?.required as string[] | undefined) ?? [];
  const complete = required.every((f) => {
    const v = draft[f];
    return v !== undefined && v !== null && v !== '';
  });
  return (
    <Box sx={{ width: 440, p: 2 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Add {spec.label.replace(/s$/, '').toLowerCase()}
      </Typography>
      <SchemaForm
        spec={spec}
        values={draft}
        onChange={(field, value) => setDraft((d) => ({ ...d, [field]: value }))}
      />
      <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
        <Button variant="contained" size="small" disabled={!complete} onClick={() => onSubmit(draft)}>
          Stage record
        </Button>
        <Button size="small" onClick={onCancel}>
          Cancel
        </Button>
      </Stack>
    </Box>
  );
}
